<script lang="ts">
  import { Editor, EditorContent } from "@tiptap/vue-3";
  import StarterKit from "@tiptap/starter-kit";
  import Underline from "@tiptap/extension-underline";
  import { Field as ValidationProvider } from "vee-validate";
  import { Component, Vue, Prop, Watch, Ref } from "vue-facing-decorator";
  import { icons } from "@/assets/svg";

  @Component({
    components: {
      ValidationProvider,
      EditorContent,
      IconBold: icons.BoldIcon,
      IconUnderline: icons.UnderlineIcon,
      IconItalic: icons.ItalicIcon,
      IconStrikethrough: icons.StrikeIcon,
    },
  })
  export default class FormWysiwyg extends Vue {
    @Prop() name?: string;

    @Prop() value?: string;

    @Prop() placeholder?: string;

    @Prop() rules?: string | object;

    @Prop() label?: string;

    @Prop({ default: " text-12" }) labelClass!: string;

    @Prop({ default: false }) disabled!: boolean;

    @Prop({ default: "text" }) type!: string;

    @Ref() input!: HTMLInputElement;

    @Ref() readonly provider!: InstanceType<typeof ValidationProvider>;

    isFocussed = false;

    validAtleastOnce = false;

    editor: Editor | null = null;

    created() {
      this.initEditor();
    }

    async initEditor() {
      this.editor = new Editor({
        content: this.value,
        editorProps: {
          attributes: {
            class:
              "prose caret-secondary-500 max-w-none border border-gray-50 hover:border-secondary-300 focus:border-secondary-500 focus:outline-none min-h-[120px] transition-colors rounded-[8px] pt-[8px] pb-[48px] px-[12px]",
          },
        },
        extensions: [StarterKit, Underline],
        onUpdate: () => {
          if (!this.editor) {
            return;
          }

          this.$emit("input", this.editor.getHTML());
        },
      });
    }

    private get validateAs(): string {
      if (this.label) {
        return this.label;
      }

      if (this.placeholder) {
        return this.placeholder;
      }

      if (this.name) {
        return this.name;
      }

      return `${this.type} field`;
    }

    beforeDestroy() {
      if (!this.editor) {
        return;
      }

      this.editor.destroy();
    }

    convertHTML(str: string) {
      return str;
      let doc = new DOMParser().parseFromString(str, "text/html");
      return doc.documentElement.textContent;
    }

    @Watch("value", { immediate: true })
    private valueChanged(newValue: string) {
      if (!this.editor) {
        return;
      }

      const isSame = this.editor.getHTML() === this.convertHTML(newValue);

      if (isSame) {
        return;
      }

      this.editor.commands.setContent(this.convertHTML(newValue), false);
    }
  }
</script>

<template>
  <ValidationProvider
    ref="provider"
    :vid="name"
    :name="validateAs"
    :rules="rules"
    :bails="false"
    v-slot="{ meta, errors }"
    tag="div"
    :class="{ 'opacity-75 disabled cursor-not-allowed': disabled, 'is-focussed': isFocussed }"
  >
    <label v-if="label" :for="`id_${name}`" :class="[meta.touched ? 'text-gray-900' : 'text-gray-300', labelClass]" class="block mb-4">
      {{ label }}
      <span v-if="(rules && typeof rules === 'string' && rules.includes('required')) || (typeof rules === 'object' && rules.required)">*</span>
    </label>
    <div :class="{ 'pointer-events-none': disabled }" class="relative">
      <editor-content :editor="editor" />
      <div class="absolute bottom-0 z-10 px-[8px] pb-[8px] flex items-ce justify-between w-full" v-if="editor">
        <div class="flex items-end flex-wrap">
          <button type="button" class="p-[4px] transition-colors text-gray-300" :class="{ 'text-black': editor.isActive('bold') }" @click="editor.chain().focus().toggleBold().run()">
            <IconBold class="fill-current h-[12px]"></IconBold>
          </button>
          <button
            type="button"
            class="p-[4px] transition-colors text-gray-300"
            :class="{ 'text-black': editor.isActive('underline') }"
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <IconUnderline class="fill-current h-[12px]"></IconUnderline>
          </button>
          <button
            type="button"
            class="p-[4px] transition-colors text-gray-300"
            :class="{ 'text-black': editor.isActive('italic') }"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <IconItalic class="fill-current h-[12px]"></IconItalic>
          </button>
          <button
            type="button"
            class="p-[4px] transition-colors text-gray-300"
            :class="{ 'text-black': editor.isActive('strike') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <IconStrikethrough class="fill-current h-[12px]"></IconStrikethrough>
          </button>
        </div>
      </div>
    </div>

    <span v-if="errors[0] && meta.dirty && meta.touched" class="text-red text-12 block mt-2">{{ errors[0] }}</span>
  </ValidationProvider>
</template>

<style scoped lang="scss">
  label {
    @apply text-gray-900 font-semibold text-12 mb-2;
  }

  .disabled {
    textarea {
      @apply bg-gray-50;
    }
  }
</style>
